import { FC, createContext, useContext, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { NotificationAlertType } from '~globals/types/enums';
import {
  useParseFiltersRequestSearchItems,
  useSelectionIds,
  useDataTableRequest,
  useToggleDialogConfirmation,
  useToggle,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';
import AlertSnackbarContent from '~components/AlertSnackbarContent';
import { PATHS } from '~constants/paths';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from '../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';

import {
  DashboardVariantV2CarrierContentFinalizedTabContextValue,
  DashboardVariantV2CarrierContentFinalizedTabDialogDetailsOrderData,
  DashboardVariantV2CarrierContentFinalizedTabDialogChangeStateOrderData,
} from './types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
} from './utils';

const DashboardVariantV2CarrierContentFinalizedTabContext =
  createContext<DashboardVariantV2CarrierContentFinalizedTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    isOnlyTodayDelivery: {
      value: false,
      toggleValue: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleShowSuccessAlert: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      feedback: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      markAsPending: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      setFeedbackStop: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      changeStateOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      rebillOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
      modifyAssignmentOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2CarrierContentFinalizedTabContext =
  (): DashboardVariantV2CarrierContentFinalizedTabContextValue =>
    useContext(DashboardVariantV2CarrierContentFinalizedTabContext);

const DashboardVariantV2CarrierContentFinalizedTabProvider: FC = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    activeTab: { currentTab, setActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const { filters, forceRequest } =
    useDashboardVariantV2CarrierFinalizedFiltersContext();

  const { selectedPreset } = useDashboardVariantV2CarrierPresetsContext();

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: filters.filters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultCollectItemState: [],
  });

  const [isOnlyTodayDelivery, toggleIsOnlyTodayDelivery] = useToggle(false);

  const selection = useSelectionIds<string>([]);

  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
  });

  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      table.handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, selection.utils, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    if (table.queryParams.pagination.page === DEFAULT_PAGINATION.page) {
      table.reloadRequest();
    } else {
      table.handleChangeTable('filter', {
        pagination: table.queryParams.pagination,
        filters: parseFiltersRequest(),
      });
    }
  }, [selection.utils, table, parseFiltersRequest]);

  const handleAlertButtonAction = useCallback(() => {
    filters.handleClearFilters(false);
    selectedPreset.reset();

    setActiveTab('inWarehouse');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.handleClearFilters, selectedPreset, setActiveTab]);

  const handleShowSuccessAlert = useCallback(
    (title: string, message: string) => {
      enqueueSnackbar(title, {
        variant: 'success',
        content: (key, currentTitle) => (
          <AlertSnackbarContent
            id={key}
            title={currentTitle}
            message={message}
            type={NotificationAlertType.Success}
            link={{
              message: 'Ver pedidos pendientes',
              path: PATHS.ORDERS.BASE,
            }}
            buttonAction={handleAlertButtonAction}
            initialExpanded={true}
          />
        ),
      });

      reloadDataAndResetSelectedRows();
    },
    [enqueueSnackbar, handleAlertButtonAction, reloadDataAndResetSelectedRows],
  );

  const [
    openDialogFeedback,
    selectedDataForFeedback,
    handleOpenDialogFeedback,
    handleCloseDialogFeedback,
  ] =
    useToggleDialogConfirmation<Nullable<{ stopId: string; orderId: string }>>(
      null,
    );

  const [
    openDialogMarkAsPendingSelectedOrder,
    selectedMarkAsPendingOrder,
    handleOpenDialogConfirmMarkAsPendingSelectedOrder,
    handleCloseDialogConfirmMarkAsPendingSelectedOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogSetFeedbackStop,
    selectedItemSetFeedbackStop,
    handleOpenDialogSetFeedbackStop,
    handleCloseDialogSetFeedbackStop,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentFinalizedTabDialogDetailsOrderData>
    >(null);

  const [
    openDialogChangeStateOrder,
    selectedOrderDataToChangeState,
    handleOpenDialogChangeStateOrder,
    handleCloseDialogChangeStateOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2CarrierContentFinalizedTabDialogChangeStateOrderData>
    >(null);

  const [
    openDialogRebillOrder,
    selectedItemIdToRebill,
    handleOpenDialogRebillOrder,
    handleCloseDialogRebillOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const [
    openDialogModifyAssignmentOrder,
    selectedItemIdToModifyAssignment,
    handleOpenDialogModifyAssignmentOrder,
    handleCloseDialogModifyAssignmentOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  return (
    <DashboardVariantV2CarrierContentFinalizedTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        table,
        isOnlyTodayDelivery: {
          value: isOnlyTodayDelivery,
          toggleValue: toggleIsOnlyTodayDelivery,
        },
        actions: {
          reloadDataAndResetSelectedRows,
          handleShowSuccessAlert,
          handleForceRequests,
        },
        dialogs: {
          feedback: {
            open: openDialogFeedback,
            data: selectedDataForFeedback,
            handleOpen: handleOpenDialogFeedback,
            handleClose: handleCloseDialogFeedback,
          },
          markAsPending: {
            open: openDialogMarkAsPendingSelectedOrder,
            data: selectedMarkAsPendingOrder,
            handleOpen: handleOpenDialogConfirmMarkAsPendingSelectedOrder,
            handleClose: handleCloseDialogConfirmMarkAsPendingSelectedOrder,
          },
          setFeedbackStop: {
            open: openDialogSetFeedbackStop,
            data: selectedItemSetFeedbackStop,
            handleOpen: handleOpenDialogSetFeedbackStop,
            handleClose: handleCloseDialogSetFeedbackStop,
          },
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
          changeStateOrder: {
            open: openDialogChangeStateOrder,
            data: selectedOrderDataToChangeState,
            handleOpen: handleOpenDialogChangeStateOrder,
            handleClose: handleCloseDialogChangeStateOrder,
          },
          rebillOrder: {
            open: openDialogRebillOrder,
            data: selectedItemIdToRebill,
            handleOpen: handleOpenDialogRebillOrder,
            handleClose: handleCloseDialogRebillOrder,
          },
          modifyAssignmentOrder: {
            open: openDialogModifyAssignmentOrder,
            data: selectedItemIdToModifyAssignment,
            handleOpen: handleOpenDialogModifyAssignmentOrder,
            handleClose: handleCloseDialogModifyAssignmentOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentFinalizedTabContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentFinalizedTabProvider;
